import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/atoms/Button';
import { Text } from '@/components/atoms/Text';
import { CancelOrderReason } from '@/services/innovorder/orders/order.enum';
import { ManualPaymentMethodCode } from '@/services/innovorder/orders/order.type';
import { Textarea } from '@/components/atoms/Form/Textarea';
import Loader from '@/components/atoms/Loader';
import Modal from '../../../../components/atoms/Modal';
import Select from '../../../../components/atoms/Select';
import { Container, TextareaContainer } from './OrderDetailsCancelConfirmation.style';
import { useOrderDetailsCancelConfirmationVM } from './OrderDetailsCancelConfirmation.viewmodel';

export type OrderDetailsCancelConfirmationProps = {
    cancelWithContraEntry: boolean;
    hasManualPaymentMethodToChoose: boolean;
    defaultManualPaymentMethodCode: ManualPaymentMethodCode;
    channelId: number;
    restaurantId: number;
    consumptionModeId: number;
    onHide: () => void;
    onValidate: (
        reason: CancelOrderReason,
        cancellationReasonDescription?: string,
        manualPaymentMethodCode?: ManualPaymentMethodCode,
    ) => void;
};

const OrderDetailsCancelConfirmation: React.FunctionComponent<
    React.PropsWithChildren<OrderDetailsCancelConfirmationProps>
> = ({
    cancelWithContraEntry,
    hasManualPaymentMethodToChoose,
    defaultManualPaymentMethodCode,
    onHide,
    onValidate,
    channelId,
    restaurantId,
    consumptionModeId,
}) => {
    const {
        CancelOrderReasons,
        cancelReason,
        handleChangeReason,
        manualPaymentMethodCode,
        handleChangeManualPaymentMethodCode,
        isDisabled,
        handleValidate,
        step,
        ManualPaymentMethodCodes,
        cancellationReasonDescription,
        handleChangeCancellationReasonDescription,
        isLoadingManualPaymentMethodCodeList,
    } = useOrderDetailsCancelConfirmationVM({
        hasManualPaymentMethodToChoose,
        defaultManualPaymentMethodCode,
        onValidate,
        cancelWithContraEntry,
        onHide,
        channelId,
        restaurantId,
        consumptionModeId,
    });

    const displayLoading = useMemo(() => {
        return <Loader withContainer />;
    }, []);

    const displayReasonStep = useMemo(() => {
        return (
            <>
                <Container>
                    <Text text="orderDetail.cancel.text1" />
                    <Text text="orderDetail.cancel.text2" />
                    <Text text="orderDetail.cancel.text3" />
                    {!cancelWithContraEntry && (
                        <>
                            <Text text="orderDetail.cancel.text4" />
                            <Text text="orderDetail.cancel.text5" />
                        </>
                    )}
                </Container>
                <Select
                    value={cancelReason}
                    options={CancelOrderReasons}
                    onChange={handleChangeReason}
                    solidListbox
                    placeholder="orderDetail.cancel.reason.placeholder"
                />
                <TextareaContainer>
                    <Textarea
                        value={cancellationReasonDescription}
                        onChange={handleChangeCancellationReasonDescription}
                        placeholder="orderDetail.cancel.reason.description.placeholder"
                        maxLength={250}
                        labelId="orderDetail.cancel.reason.description.label"
                    />
                </TextareaContainer>
            </>
        );
    }, [
        CancelOrderReasons,
        cancelReason,
        cancelWithContraEntry,
        handleChangeReason,
        cancellationReasonDescription,
        handleChangeCancellationReasonDescription,
    ]);

    const displayManualPaymentMethodStep = useMemo(() => {
        return (
            <>
                <Container>
                    <Text text="orderDetail.cancel.text6" />
                    <Text text="orderDetail.cancel.text7" />
                </Container>
                <Select
                    value={manualPaymentMethodCode}
                    options={ManualPaymentMethodCodes ?? []}
                    onChange={handleChangeManualPaymentMethodCode}
                    solidListbox
                    placeholder="orderDetail.cancel.manualPaymentMethod.placeholder"
                />
            </>
        );
    }, [manualPaymentMethodCode, ManualPaymentMethodCodes, handleChangeManualPaymentMethodCode]);

    return (
        <Modal
            isOpen
            onHide={onHide}
            title={`orderDetail.cancel.title.${step}`}
            buttons={
                <Button disabled={isDisabled} onClick={handleValidate}>
                    <FormattedMessage
                        id={step === 'reason' ? `orderDetail.cancel.confirm` : `orderDetail.cancel.validate`}
                    />
                </Button>
            }
        >
            {isLoadingManualPaymentMethodCodeList ? displayLoading : null}
            {!isLoadingManualPaymentMethodCodeList && step === 'reason' ? displayReasonStep : null}
            {!isLoadingManualPaymentMethodCodeList && step === 'manualPaymentMethod'
                ? displayManualPaymentMethodStep
                : null}
        </Modal>
    );
};

export default React.memo(OrderDetailsCancelConfirmation);
